import React, {useEffect, useMemo, useState} from 'react';
import {Link, useParams, useSearchParams} from 'react-router-dom';
import type {Product} from './ProductResultTable';
import WekaAddToCart from './WekaAddToCart';
import MyAccountPageShell from './MyAccountPageShell';
import { LoadingBar } from './MyAccountLoadingStates';
import ProductDetailSurface from './ProductDetailSurface';
import { buildProductDetailAddToCartProps } from '../utils/buildProductDetailAddToCartProps';
import {
    emptyProductDetailResponse,
    fetchProductDetail,
    type ProductDetailResponse,
} from '../utils/productDetailApi';

const DEFAULT_BACK_PATH = '/find-products';

const normalizeBackPath = (value: string | null): string => {
    const normalized = String(value || '').trim();
    if (normalized.startsWith('/find-products')) {
        return normalized;
    }

    return DEFAULT_BACK_PATH;
};

const B2BProductDetailPage: React.FC = () => {
    const {productIdentifier} = useParams<{productIdentifier: string}>();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [searchResponse, setSearchResponse] = useState<ProductDetailResponse>(emptyProductDetailResponse);

    const backPath = useMemo(() => normalizeBackPath(searchParams.get('return')), [searchParams]);
    const resolvedProductIdentifier = useMemo(() => String(productIdentifier || '').trim(), [productIdentifier]);

    useEffect(() => {
        if (resolvedProductIdentifier === '') {
            setIsLoading(false);
            setErrorMessage('Invalid product identifier.');
            setSearchResponse(emptyProductDetailResponse);
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        const abortController = new AbortController();

        fetchProductDetail(resolvedProductIdentifier, abortController.signal)
            .then((payload) => {
                setSearchResponse(payload);
            })
            .catch((error) => {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return;
                }

                console.error('Failed to load product detail:', error);
                setErrorMessage('Unable to load this product right now.');
                setSearchResponse(emptyProductDetailResponse);
            })
            .finally(() => {
                setIsLoading(false);
            });

        return () => {
            abortController.abort();
        };
    }, [resolvedProductIdentifier]);

    const product = useMemo<Product | null>(() => {
        return searchResponse.results[0] || null;
    }, [searchResponse.results]);

    const showNotFound = !isLoading && errorMessage === '' && !product;
    const backLinkTitle = String(searchResponse.settings?.header_title || '').trim() || 'Find Products';
    const backLinkLabel = `Back to ${backLinkTitle}`;

    return (
        <MyAccountPageShell
            className="find-products-detail-page"
            title={product?.name || 'Product Details'}
            subtitle={String(product?.sku || '').trim() !== '' ? `SKU: ${product?.sku}` : 'Review product details and pricing.'}
            actions={(
                <Link to={backPath} className="btn btn-outline-secondary btn-sm">
                    <i className="fas fa-arrow-left me-1"></i>
                    {backLinkLabel}
                </Link>
            )}
        >

            {errorMessage !== '' && (
                <div className="alert alert-danger" role="alert">
                    {errorMessage}
                </div>
            )}

            {showNotFound && (
                <div className="alert alert-warning" role="alert">
                    Product not found.
                </div>
            )}

            {isLoading && (
                <div className="find-products-single-product-view" aria-live="polite" aria-busy="true">
                    <div className="find-products-single-product-media">
                        <div className="find-products-single-product-image">
                            <div className="skeleton" style={{ width: '100%', minHeight: '320px', borderRadius: '12px' }} />
                        </div>
                    </div>

                    <div className="find-products-single-product-summary">
                        <div className="find-products-single-product-description">
                            <LoadingBar height="18px" width="82%" style={{ marginBottom: '12px' }} />
                            <LoadingBar height="18px" width="74%" style={{ marginBottom: '12px' }} />
                            <LoadingBar height="18px" width="68%" />
                        </div>

                        <div className="find-products-single-product-add-to-cart weka-add-to-cart-surface weka-surface weka-surface--product-add-to-cart">
                            <div className="weka-add-to-cart-surface__inner">
                                <LoadingBar height="16px" width="28%" style={{ marginBottom: '16px' }} />
                                <LoadingBar height="44px" width="180px" style={{ borderRadius: '8px', marginBottom: '16px' }} />
                                <LoadingBar height="14px" width="48%" style={{ marginBottom: '12px' }} />
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} />
                                    </div>
                                    <div className="col-md-6">
                                        <LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} />
                                    </div>
                                </div>
                                <div className="row g-2">
                                    {Array.from({ length: 4 }).map((_, index) => (
                                        <div key={`product-detail-break-skeleton-${index}`} className="col-6 col-lg-3">
                                            <div className="border rounded p-2 h-100">
                                                <LoadingBar height="12px" width="56%" style={{ marginBottom: '8px' }} />
                                                <LoadingBar height="18px" width="72%" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!isLoading && product && (
                <ProductDetailSurface
                    product={product}
                    settings={searchResponse.settings}
                    imageLoading="eager"
                    addToCart={(
                        <WekaAddToCart {...buildProductDetailAddToCartProps(product, searchResponse.settings)} />
                    )}
                />
            )}
        </MyAccountPageShell>
    );
};

export default B2BProductDetailPage;
